import * as React from 'react';

import { Button, ButtonProps } from '@playbooks/ui/components/button';
import { FarIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
	// Theme
	const rootClassName = cn('flex w-full justify-start', className);

	// Render
	return <nav aria-label='pagination' data-slot='pagination' className={rootClassName} {...props} />;
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
	// Theme
	const rootClassName = cn('flex items-center gap-1', className);

	// Render
	return <ul data-slot='pagination-content' className={rootClassName} {...props} />;
}

function PaginationEllipsis() {
	return (
		<span aria-hidden className='flex size-7 items-center justify-center text-sm text-foreground-body'>
			…
		</span>
	);
}

function PaginationItem(props: React.ComponentProps<'li'>) {
	return <li data-slot='pagination-item' {...props} />;
}

type PaginationButtonProps = ButtonProps & { active?: boolean };

function PaginationButton({ active, className, ...props }: PaginationButtonProps) {
	return (
		<Button
			aria-current={active ? 'page' : undefined}
			data-active={active || undefined}
			variant={active ? 'accent' : 'ghost'}
			size='icon-xs'
			className={className}
			{...props}
		/>
	);
}

function PaginationFirst(props: ButtonProps) {
	return (
		<PaginationButton aria-label='Go to first page' {...props}>
			<FarIcon icon='chevrons-left' className='size-3 text-foreground-subtle' />
		</PaginationButton>
	);
}

function PaginationPrevious(props: ButtonProps) {
	return (
		<PaginationButton aria-label='Go to previous page' {...props}>
			<FarIcon icon='chevron-left' className='size-3 text-foreground-subtle' />
		</PaginationButton>
	);
}

function PaginationNext(props: ButtonProps) {
	return (
		<PaginationButton aria-label='Go to next page' {...props}>
			<FarIcon icon='chevron-right' className='size-3 text-foreground-subtle' />
		</PaginationButton>
	);
}

function PaginationLast(props: ButtonProps) {
	return (
		<PaginationButton aria-label='Go to last page' {...props}>
			<FarIcon icon='chevrons-right' className='size-3 text-foreground-subtle' />
		</PaginationButton>
	);
}

export {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationButton,
	PaginationFirst,
	PaginationPrevious,
	PaginationNext,
	PaginationLast,
	PaginationEllipsis,
};
